// Shared helpers for every email signup point: first-touch attribution, subscriber memory and analytics.
window.emailSignup ??= (() => {
  const STATE_KEY = 'ourmoney:email-signup';
  const ATTRIBUTION_KEY = 'ourmoney:attribution';
  const ATTRIBUTION_TTL_MS = 30 * 24 * 60 * 60 * 1000;
  const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content'];
  const REFERRERS = [
    ['instagram', 'instagram'],
    ['tiktok', 'tiktok'],
    ['facebook', 'facebook'],
    ['fb.', 'facebook'],
    ['youtube', 'youtube'],
    ['youtu.be', 'youtube'],
    ['pinterest', 'pinterest'],
    ['snapchat', 'snapchat'],
    ['reddit', 'reddit'],
    ['t.co', 'twitter'],
    ['twitter', 'twitter'],
    ['x.com', 'twitter'],
    ['google.', 'google'],
    ['bing.', 'bing'],
  ];

  // localStorage can be missing or throw (private browsing, blocked storage); the site must keep working.
  const storage = (() => {
    try {
      const probe = '__ourmoney_probe__';
      window.localStorage.setItem(probe, probe);
      window.localStorage.removeItem(probe);
      return window.localStorage;
    } catch {
      return null;
    }
  })();

  const read = (key) => {
    try {
      return JSON.parse(storage?.getItem(key) ?? 'null');
    } catch {
      return null;
    }
  };

  const write = (key, value) => {
    try {
      storage?.setItem(key, JSON.stringify(value));
    } catch {
      /* storage full or blocked: nothing to persist */
    }
  };

  const handle = (value) =>
    String(value)
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 40);

  const referrerChannel = () => {
    if (!document.referrer) return 'direct';
    let host;
    try {
      host = new URL(document.referrer).hostname;
    } catch {
      return 'direct';
    }
    if (host === location.hostname) return null;
    return REFERRERS.find(([needle]) => host.includes(needle))?.[1] ?? 'other';
  };

  function captureAttribution() {
    const params = new URLSearchParams(location.search);
    const utm = {};
    UTM_KEYS.forEach((key) => {
      const value = params.get(key) && handle(params.get(key));
      if (value) utm[key] = value;
    });
    if (Object.keys(utm).length) {
      write(ATTRIBUTION_KEY, { ...utm, at: Date.now() });
      return;
    }
    const stored = read(ATTRIBUTION_KEY);
    if (stored && Date.now() - stored.at < ATTRIBUTION_TTL_MS) return;
    const channel = referrerChannel();
    if (channel) write(ATTRIBUTION_KEY, { referrer: channel, at: Date.now() });
  }

  function attribution() {
    const stored = read(ATTRIBUTION_KEY);
    if (!stored || Date.now() - stored.at >= ATTRIBUTION_TTL_MS) return {};
    const { at, ...fields } = stored;
    return fields;
  }

  function attributionTags() {
    return Object.entries(attribution()).map(([key, value]) =>
      key === 'referrer' ? `ref-${value}` : `${key.replace('_', '-')}-${value}`
    );
  }

  function publish(name, data = {}) {
    try {
      window.Shopify?.analytics?.publish?.(name, { ...data, ...attribution() });
    } catch {
      /* analytics must never break signup */
    }
  }

  return {
    // The hidden frame that signups post into loads the same page; everything must stay inert there.
    inSignupFrame: window.name.startsWith('email-signup-frame'),
    captureAttribution,
    attributionTags,
    publish,
    isSubscribed: () => read(STATE_KEY)?.state === 'subscribed',
    rememberSubscribed: () => write(STATE_KEY, { state: 'subscribed', at: Date.now() }),
    read,
    write,
  };
})();

if (!window.emailSignup.inSignupFrame) window.emailSignup.captureAttribution();

// Signups submit natively (Shopify's CAPTCHA hooks the native submit and rejects fetch()), but into a hidden
// frame, so the visitor stays where they are and the result is read from Shopify's rendered form.
if (!window.emailSignup.inSignupFrame && !customElements.get('email-signup')) {
  customElements.define(
    'email-signup',
    class EmailSignup extends HTMLElement {
      static TIMEOUT_MS = 30000;

      connectedCallback() {
        this.form = this.querySelector('form');
        if (!this.form) return;
        this.input = this.form.querySelector('input[type="email"]');
        this.button = this.form.querySelector('button[type="submit"]');
        this.status = this.form.querySelector('[role="status"]');
        this.tags = this.form.querySelector('input[name="contact[tags]"]');
        this.baseTags = this.tags.value;

        this.frame = document.createElement('iframe');
        this.frame.name = `email-signup-frame-${this.form.id}`;
        this.frame.title = '';
        this.frame.hidden = true;
        this.frame.tabIndex = -1;
        this.frame.setAttribute('aria-hidden', 'true');
        this.append(this.frame);
        this.form.target = this.frame.name;

        this.onSubmit = this.onSubmit.bind(this);
        this.onButtonClick = this.onButtonClick.bind(this);
        this.onFrameLoad = this.onFrameLoad.bind(this);
        this.onInput = () => this.input.removeAttribute('aria-invalid');
        this.onPageShow = () => this.setBusy(false);

        this.form.addEventListener('submit', this.onSubmit);
        this.button.addEventListener('click', this.onButtonClick);
        this.input.addEventListener('input', this.onInput);
        window.addEventListener('pageshow', this.onPageShow);

        this.reportOutcome();
      }

      disconnectedCallback() {
        this.form?.removeEventListener('submit', this.onSubmit);
        this.button?.removeEventListener('click', this.onButtonClick);
        this.input?.removeEventListener('input', this.onInput);
        this.frame?.removeEventListener('load', this.onFrameLoad);
        window.removeEventListener('pageshow', this.onPageShow);
        clearTimeout(this.timeout);
      }

      // Result of a full-page (fallback) submission: Shopify redirects back to #form-id with the form's state rendered.
      get outcome() {
        if (location.hash !== `#${this.form.id}`) return null;
        if (this.status.querySelector('.newsletter-form__message--success')) return 'success';
        if (this.status.querySelector('.form__message')) return 'error';
        return null;
      }

      reportOutcome() {
        const outcome = this.outcome;
        if (outcome) this.announce(outcome);
      }

      // Repeat clicks (and Enter, which clicks the default button) are ignored while a signup is in flight.
      // Submit events are never cancelled, because Shopify's CAPTCHA re-submits the form itself.
      onButtonClick(event) {
        if (this.busy) event.preventDefault();
      }

      onSubmit() {
        this.input.value = this.input.value.trim().toLowerCase();
        this.tags.value = [this.baseTags, ...window.emailSignup.attributionTags()].join(', ');
        window.emailSignup.publish('email_signup_submitted', { form: this.dataset.source });
        this.setBusy(true);
        this.frame.addEventListener('load', this.onFrameLoad, { once: true });
        this.timeout = setTimeout(() => {
          this.frame.removeEventListener('load', this.onFrameLoad);
          this.fail(this.dataset.msgError);
        }, EmailSignup.TIMEOUT_MS);
      }

      onFrameLoad() {
        clearTimeout(this.timeout);
        let page;
        let url = '';
        try {
          page = this.frame.contentDocument;
          url = this.frame.contentWindow.location.href;
        } catch {
          page = null;
        }
        const rendered = page?.getElementById(this.form.id)?.querySelector('[role="status"]');
        const success = rendered?.querySelector('.newsletter-form__message--success');
        const error = rendered?.querySelector('.form__message:not(.newsletter-form__message--success)');

        if (success || (!error && url.includes('customer_posted=true'))) {
          this.setBusy(false);
          this.form.reset();
          this.showMessage(success);
          this.announce('success');
        } else if (error) {
          this.input.setAttribute('aria-invalid', 'true');
          this.fail(null, error);
          this.input.focus();
        } else {
          // Anything else (e.g. Shopify's "Verifying your connection" check) needs the real page, so
          // repeat the signup as a normal page submit. form.submit() goes through Shopify's CAPTCHA wrapper.
          this.form.removeAttribute('target');
          this.form.submit();
        }
      }

      announce(outcome) {
        if (outcome === 'success') {
          window.emailSignup.rememberSubscribed();
          window.emailSignup.publish('email_signup_succeeded', { form: this.dataset.source });
        } else {
          window.emailSignup.publish('email_signup_failed', { form: this.dataset.source });
        }
        this.dispatchEvent(new CustomEvent('email-signup:result', { bubbles: true, detail: { outcome } }));
      }

      fail(text, renderedMessage) {
        this.setBusy(false);
        if (renderedMessage) {
          this.showMessage(renderedMessage);
        } else {
          const message = document.createElement('p');
          message.className = 'newsletter-form__message form__message';
          message.textContent = text;
          this.status.replaceChildren(message);
        }
        this.announce('error');
      }

      showMessage(renderedMessage) {
        if (!renderedMessage) {
          this.status.textContent = '';
          return;
        }
        const message = document.importNode(renderedMessage, true);
        message.removeAttribute('autofocus');
        message.removeAttribute('tabindex');
        this.status.replaceChildren(message);
      }

      setBusy(busy) {
        this.busy = busy;
        this.classList.toggle('is-busy', busy);
        this.form.setAttribute('aria-busy', String(busy));
        this.button.setAttribute('aria-disabled', String(busy));
        if (busy) this.status.textContent = this.dataset.msgSubmitting;
      }
    }
  );
}
